import { type KeyboardEvent, useState } from 'react';

/**
 * The keyboard of a `radiogroup` (retour-experience.md §4.1, §4.2): the Sélection
 * and the Note are the same control twice - a row of radios of which at most one
 * is lit - and read the same keys.
 *
 * « Accessibles au clavier comme un groupe de boutons radio » : ONE tab stop for
 * the whole group (the lit radio, or the first when none is), the arrows move
 * between the radios - wrapping at both ends, `Home` and `End` going to the
 * ends - and Espace or Entrée lights the one that holds the focus, which is a
 * `<button>`'s own click and needs nothing here.
 *
 * The arrows MOVE the focus and light nothing: on a Sélection that may be left
 * unanswered, a key that wrote a value on every step would answer for the
 * person.
 *
 * `cursor` is the radio the focus last landed on. Without it, the tab stop would
 * stay on the lit radio after an arrow, and the next Tab would walk to it from
 * wherever the arrows left the focus instead of leaving the group.
 */
export interface RadioGroupKeys {
  /** The `tabIndex` of the radio at this place. */
  readonly tabIndexOf: (index: number) => 0 | -1;
  /** To spread on every radio: records where the focus is. */
  readonly onFocusOf: (index: number) => () => void;
  /** To spread on the group. */
  readonly onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

export function useRadioGroupKeys(count: number, lit: number | null): RadioGroupKeys {
  const [cursor, setCursor] = useState<number | null>(null);
  const stop = Math.min(cursor ?? lit ?? 0, Math.max(count - 1, 0));

  return {
    tabIndexOf: (index) => (index === stop ? 0 : -1),
    onFocusOf: (index) => () => setCursor(index),
    onKeyDown: (event) => {
      const radios = [...event.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]')];
      const at = radios.findIndex((radio) => radio === document.activeElement);
      if (at === -1) return;
      const last = radios.length - 1;
      let to: number;
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          to = at === last ? 0 : at + 1;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          to = at === 0 ? last : at - 1;
          break;
        case 'Home':
          to = 0;
          break;
        case 'End':
          to = last;
          break;
        default:
          return;
      }
      event.preventDefault();
      radios[to]?.focus();
    },
  };
}
